import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ContentChild,
    inject,
    input,
    output,
    TemplateRef,
    ViewEncapsulation,
    Input,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { IContentLanguageLabelModel } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared';
import { FiContentLanguageSharedService } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared.service';
import {
    FiTooltipButtonIconColors,
    FiTooltipIconClassType,
} from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { ISection, ISectionsDndEditorConfig } from '../models/sections-dnd-editor.models';

@Component({
    selector: 'fi-section-dnd-items',
    templateUrl: './fi-section-dnd-items.component.html',
    styleUrls: ['./fi-section-dnd-items.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-section-dnd-items' },
    standalone: false,
})
export class FiSectionDndItemsComponent {
    section = input.required<ISection>();
    sectionChange = output<ISection>();
    sectionIndex = input.required<number>();
    selectedLanguageId = input.required<number>();
    sectionItemsAmount = input<number>();
    sectionItemTemplate = input<TemplateRef<any>>();
    sectionBottomContentTemplate = input<TemplateRef<any>>();
    onAddSectionItem = output<number>();
    dragItemClassName = input<string>();
    sectionsDndEditorConfig = input.required<ISectionsDndEditorConfig>();
    isNewUI = input<boolean>(false);

    @Input() isDeleteSectionButtonDisabledFn: (section: ISection) => boolean = () => false;

    openSectionEditorEvent = output<number>();
    openConfirmDeleteSectionDialogEvent = output<number>();
    onSectionItemDrop = output<CdkDragDrop<any[]>>();

    @ContentChild('sectionItemContentTemplateRef') sectionItemContentTemplateRef: TemplateRef<any>;
    @ContentChild('sectionBottomContentTemplateRef') sectionBottomContentTemplateRef: TemplateRef<any>;

    protected readonly sectionCdkDropListId = 'section-drop-list';

    private fiContentLanguageSharedService = inject(FiContentLanguageSharedService);

    protected canDeleteSection = computed(
        () => this.sectionItemsAmount() > 1 && !this.isDeleteSectionButtonDisabledFn(this.section()),
    );
    protected sectionItems = computed(() => this.section()[this.sectionsDndEditorConfig().sectionItemsPropName]);

    protected sectionDropListId = computed(() => this.sectionCdkDropListId + this.sectionIndex());
    protected effectiveSectionItemTemplate = computed(
        () => this.sectionItemTemplate() ?? this.sectionItemContentTemplateRef,
    );
    protected effectiveSectionBottomContentTemplate = computed(
        () => this.sectionBottomContentTemplate() ?? this.sectionBottomContentTemplateRef,
    );

    protected sectionItemsIds = computed(() => {
        const amount = this.sectionItemsAmount() || 0;
        const idsArr = [];
        new Array(amount).fill('').forEach((el, index) => {
            idsArr.push(this.sectionCdkDropListId + index);
        });
        return idsArr;
    });

    protected sectionTitle = computed(() => {
        const sectionNames = this.section().Names;
        return (
            this.getCurrentContentLanguageLabelModelValue(sectionNames) ||
            this.getPrioritizedContentLanguageLabelValue(sectionNames) ||
            this.sectionsDndEditorConfig().sectionPlaceholder
        );
    });

    private getCurrentContentLanguageLabelModelValue(contentLanguageLabelModels: IContentLanguageLabelModel[]): string {
        return this.getCurrentContentLanguageLabelModel(contentLanguageLabelModels);
    }

    private getPrioritizedContentLanguageLabelValue(contentLanguageLabelModels: IContentLanguageLabelModel[]): string {
        return this.fiContentLanguageSharedService.getPrioritizedContentLanguageLabelValue({
            contentLanguageLabelModels: contentLanguageLabelModels,
            currentSelectedLanguageId: this.selectedLanguageId(),
            isLanguageNameShown: true,
        });
    }

    private getCurrentContentLanguageLabelModel(contentLanguageLabelModels: IContentLanguageLabelModel[]): string {
        return contentLanguageLabelModels.find((contentLanguageLabelModel: IContentLanguageLabelModel) => {
            return contentLanguageLabelModel.ContentLanguageId === this.selectedLanguageId();
        })?.Value;
    }

    protected elementDrop($event: CdkDragDrop<any[]>): void {
        const config = this.sectionsDndEditorConfig();
        const currentSection = this.section();
        const itemsPropName = config.sectionItemsPropName;

        if ($event.previousContainer === $event.container) {
            const currentItems = currentSection[itemsPropName] || [];
            const updatedItems = [...currentItems];
            moveItemInArray(updatedItems, $event.previousIndex, $event.currentIndex);

            const updatedSection = {
                ...currentSection,
                [itemsPropName]: updatedItems,
            };

            this.sectionChange.emit(updatedSection);
        } else {
            this.onSectionItemDrop.emit($event);
        }
    }

    protected readonly FiTooltipIconClassType = FiTooltipIconClassType;
    protected readonly FiTooltipButtonIconColors = FiTooltipButtonIconColors;
    protected readonly FiInfoIconMode = FiInfoIconMode;
}
